import React, { FC, useContext, useState } from 'react';
import { format } from 'date-fns';
import TableWithColumnSelector, { OnClickFn, TableType } from '../../core/TableWithColumnSelector';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useQuery } from '@apollo/client';
import {
  analyticsConfigQuery,
  physicianUtilizationAnalyticsQuery,
  physicianUtilizationAnalyticsTotalsQuery,
} from '../../../graph/dashboard';
import { PhysicianUtilizationTableData, PhysicianUtilizationTableTotalsData } from '../../../types/Analytics';
import { getMinutes } from './util';
import PhysicianPacuPatientsAnalytics from './PhysicianPacuPatientsAnalytics';
import Button from '@material-ui/core/Button';
import { ArrowBack, TableChart } from '@material-ui/icons';
import { AppBar, CircularProgress, Dialog, Grid, IconButton, Paper, Toolbar, useTheme } from '@material-ui/core';
import PacuJourneyChart from './v2/PacuJourneyChart';
import { useScope } from '../../../hooks/useScope';
import { PieChartData } from './v2/PieChart';
import { AggregationContext } from './Aggregation';
import AnalyticsPanel from './components/AnalyticsPanel';
import { pickFilter, pickCptFilter, isCptFilterActive } from './GeneralAnalytics';
import { CptEligibleCaption } from './MetricCptConditioning';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PacuDelaysBreakdown from './v2/PacuDelaysBreakdown';
import { DelayReason } from './v2/DelaysBreakdown';
import { PacuGroupByContext } from './PacuGroupBy';
import PacuMilestoneBarChart, { PacuMilestoneBarPoint } from './v2/PacuMilestoneBarChart';
import Configuration from './components/Configuration';
import {
  DEFAULT_PACU_STEP_NAMES,
  PacuOptionalProcedureSteps,
  PacuStepNames,
  usePacuOptionalProcedureSteps,
} from '../../../hooks/usePacuOptionalProcedureSteps';
import { pacuJourneyMetrics } from './pacuJourneyMetrics';

const formatPercentage = (value?: number | null) =>
  value === null || value === undefined || !isFinite(value) ? '-' : `${value.toFixed(1)}%`;

const formatCount = (value?: number | null) => (value === null || value === undefined ? '-' : value.toString());

const sortableLastName = (fullName: string) => {
  if (!fullName) return '';
  const suffixes = ['Jr', 'Sr', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  const parts = fullName.trim().split(/\s+/);
  if (parts.length > 1 && suffixes.includes(parts[parts.length - 1].replace(/[.,]/g, ''))) {
    return parts[parts.length - 2];
  }
  return parts[parts.length - 1];
};

const toMilestoneBarData = (
  rows: PhysicianUtilizationTableData[] | undefined,
  aggregation: string,
  avgKey: 'pacuInToTransferToChairAvg' | 'pacuInToTransferToPTAvg' | 'pacuInToPTCompleteAvg',
  medKey: 'pacuInToTransferToChairMed' | 'pacuInToTransferToPTMed' | 'pacuInToPTCompleteMed'
): PacuMilestoneBarPoint[] =>
  [...(rows || [])]
    .map(row => ({
      name: row.physician,
      value: Number(aggregation === 'average' ? row[avgKey] : row[medKey]) || 0,
    }))
    .filter(point => point.value > 0)
    .sort((a, b) => sortableLastName(a.name).localeCompare(sortableLastName(b.name)));

type PacuPhaseDelaySource = {
  oobDelays?: number | null;
  oobDelayReasons?: DelayReason[] | null;
  ptStartDelays?: number | null;
  ptStartDelayReasons?: DelayReason[] | null;
  ptCompleteDelays?: number | null;
  ptCompleteDelayReasons?: DelayReason[] | null;
  dischargeDelays?: number | null;
  dischargeDelayReasons?: DelayReason[] | null;
};

const PacuPhaseDelayCharts: FC<{
  source?: PacuPhaseDelaySource | null;
  height?: number;
  steps: PacuOptionalProcedureSteps;
}> = ({ source, height, steps }) => {
  const charts = [
    steps.transferredToChair
      ? {
          title: `${steps.names.transferredToChair} Delays`,
          count: source?.oobDelays,
          reasons: source?.oobDelayReasons,
          delayType: 'oob' as const,
        }
      : null,
    steps.transferredToPT
      ? {
          title: `${steps.names.transferredToPT} Delays`,
          count: source?.ptStartDelays,
          reasons: source?.ptStartDelayReasons,
          delayType: 'pt' as const,
        }
      : null,
    steps.ptComplete
      ? {
          title: `${steps.names.ptComplete} Delays`,
          count: source?.ptCompleteDelays,
          reasons: source?.ptCompleteDelayReasons,
          delayType: 'ptComplete' as const,
        }
      : null,
    {
      title: 'Discharge Delays',
      count: source?.dischargeDelays,
      reasons: source?.dischargeDelayReasons,
      delayType: 'pacuOut' as const,
    },
  ].flatMap(chart => (chart ? [chart] : []));

  return (
    <Grid container spacing={1} style={{ marginBottom: 8 }}>
      {charts.map(chart => (
        <Grid item xs={12} sm={6} md={3} key={chart.title} style={{ display: 'flex', minWidth: 0 }}>
          <AnalyticsPanel title={`${chart.title}${chart.count ? ` (${chart.count})` : ''}`}>
            <PacuDelaysBreakdown reasons={chart.reasons ?? []} delayType={chart.delayType} height={height} />
          </AnalyticsPanel>
        </Grid>
      ))}
    </Grid>
  );
};

const toTableData = (
  data: PhysicianUtilizationTableData[],
  totals: PhysicianUtilizationTableTotalsData,
  onClick?: OnClickFn,
  hasPostOp?: boolean,
  entityLabel: string = 'Physician',
  names: PacuStepNames = DEFAULT_PACU_STEP_NAMES
): TableType => {
  const aggregation = useContext(AggregationContext);
  const aggregationTitle = aggregation === 'average' ? 'Avg.' : 'Med.';
  const postOpHeader = hasPostOp ? [`POSTOP ${aggregationTitle}`] : [];
  const chair = names.transferredToChair;
  const pt = names.transferredToPT;
  const ptComplete = names.ptComplete;
  const baseHeaders = [
    entityLabel,
    'Procedures',
    'Surgery Days',
    `Recovery ${aggregationTitle}`,
    `Recovery In to ${chair} ${aggregationTitle}`,
    `Recovery In to ${pt} ${aggregationTitle}`,
    `Recovery In to ${ptComplete} ${aggregationTitle}`,
    `${chair} to ${pt} ${aggregationTitle}`,
    `${pt} to Recovery Exit ${aggregationTitle}`,
    `${pt} to ${ptComplete} ${aggregationTitle}`,
    `Recovery In to Discharged ${aggregationTitle}`,
    `${chair} Delays > 45 min (%)`,
    `${ptComplete} Delays`,
  ];

  const pipoOverE2EPercentage = totals?.pipoOverE2EPercentage?.toFixed(2)
    ? `${(totals?.pipoOverE2EPercentage * 100)?.toFixed(2)}%`
    : '-';
  const total = [
    totals?.physician || 'Total',
    totals?.numberOfProcedures?.toString() || '-',
    totals?.surgeryDays?.toString() || '-',
    getMinutes(aggregation === 'average' ? totals?.recoveryAvg : totals?.recoveryMed),
    getMinutes(aggregation === 'average' ? totals?.pacuInToTransferToChairAvg : totals?.pacuInToTransferToChairMed),
    getMinutes(aggregation === 'average' ? totals?.pacuInToTransferToPTAvg : totals?.pacuInToTransferToPTMed),
    getMinutes(aggregation === 'average' ? totals?.pacuInToPTCompleteAvg : totals?.pacuInToPTCompleteMed),
    getMinutes(
      aggregation === 'average' ? totals?.transferToChairToTransferToPTAvg : totals?.transferToChairToTransferToPTMed
    ),
    getMinutes(aggregation === 'average' ? totals?.transferToPTToPacuOutAvg : totals?.transferToPTToPacuOutMed),
    getMinutes(aggregation === 'average' ? totals?.transferToPTToPTCompleteAvg : totals?.transferToPTToPTCompleteMed),
    getMinutes(aggregation === 'average' ? totals?.pacuInToDischargedAvg : totals?.pacuInToDischargedMed),
    formatPercentage(totals?.pacuInToTransferToChairDelayPercentage),
    formatCount(totals?.ptCompleteDelays),
  ];

  const totalRow = {
    id: 'total',
    columns: total,
  };

  const getSortableLastName = (fullName: string) => {
    if (!fullName) return '';

    const suffixes = ['Jr', 'Sr', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

    const parts = fullName.trim().split(/\s+/);

    if (parts.length > 1 && suffixes.includes(parts[parts.length - 1].replace(/[.,]/g, ''))) {
      return parts[parts.length - 2];
    }
    return parts[parts.length - 1];
  };

  const sortedData = [...(data || [])].sort((a, b) => {
    const lastNameA = getSortableLastName(a.physician);
    const lastNameB = getSortableLastName(b.physician);
    return lastNameA.localeCompare(lastNameB);
  });

  return {
    configHeader: [...baseHeaders],
    headers: [
      {
        id: 'h1',
        columns: [...baseHeaders],
      },
    ],
    rows: sortedData
      .map(e => ({
        id: e?.id.toString(),
        columns: [
          e.physician,
          e.numberOfProcedures.toString(),
          e.surgeryDays.toString(),
          getMinutes(aggregation === 'average' ? e.recoveryAvg : e.recoveryMed),
          getMinutes(aggregation === 'average' ? e.pacuInToTransferToChairAvg : e.pacuInToTransferToChairMed),
          getMinutes(aggregation === 'average' ? e.pacuInToTransferToPTAvg : e.pacuInToTransferToPTMed),
          getMinutes(aggregation === 'average' ? e.pacuInToPTCompleteAvg : e.pacuInToPTCompleteMed),
          getMinutes(
            aggregation === 'average' ? e.transferToChairToTransferToPTAvg : e.transferToChairToTransferToPTMed
          ),
          getMinutes(aggregation === 'average' ? e.transferToPTToPacuOutAvg : e.transferToPTToPacuOutMed),
          getMinutes(aggregation === 'average' ? e.transferToPTToPTCompleteAvg : e.transferToPTToPTCompleteMed),
          getMinutes(aggregation === 'average' ? e.pacuInToDischargedAvg : e.pacuInToDischargedMed),
          formatPercentage(e.pacuInToTransferToChairDelayPercentage),
          formatCount(e.ptCompleteDelays),
        ],
        onClick,
      }))
      .concat([totalRow]),
  };
};

const toPieChartData = (totals: PhysicianUtilizationTableTotalsData): PieChartData => ({
  subtitle: isFinite(totals.numberOfProcedures)
    ? totals.numberOfProcedures === 1
      ? '1 procedure'
      : `${totals.numberOfProcedures} procedures`
    : '-',
  inOr: totals.inOrAvg,
  inSurgery: totals.inSurgeryAvg,
  closing: totals.closingAvg,
  procedureComplete: totals.procedureCompleteAvg,
  cleaning: totals.cleaningAvg,
  orReady: totals.orReadyAvg,
});

const COLORS = ['#00A7F7', '#2B3C87'];

export const CustomTooltip2 = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{payload[0].name}</Typography>
          {payload.map((p, i) => (
            <>
              <Box key={i} display="flex" alignItems="center" style={{ width: '16ch' }}>
                <Typography variant="h4">{`${Math.round(p.value)}%`}</Typography>
              </Box>
            </>
          ))}
        </Box>
      </Paper>
    );
  }

  return null;
};

const PacuPhysiciainUtilizationAnalytics: FC<{ filter: any; setFilter: any }> = ({ filter, setFilter }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const groupBy = useContext(PacuGroupByContext);
  const entityLabel = groupBy === 'ANESTHESIOLOGIST' ? 'Anesthesiologist' : 'Physician';

  const dateRangeJSON = filter?.dateRange?.toJSON();
  const dateRange = { from: format(dateRangeJSON.from, 'YYYY-MM-DD'), to: format(dateRangeJSON.to, 'YYYY-MM-DD') };
  const { operationRooms, physicians, ...rest } = filter || {};
  const cptFilterActive = isCptFilterActive(rest);

  const sanitizedOperationRoomsFilter =
    Array.isArray(operationRooms) && operationRooms.length > 0
      ? { operationRooms: operationRooms.map(or => parseInt(or, 10)) }
      : isFinite(parseInt(operationRooms, 10))
        ? { operationRooms: [parseInt(operationRooms, 10)] }
        : {};

  const sanitizedPhysiciansFilter =
    Array.isArray(physicians) && physicians.length > 0
      ? { physicians: physicians.map(py => parseInt(py, 10)) }
      : isFinite(parseInt(physicians, 10))
        ? { physicians: [parseInt(physicians, 10)] }
        : {};
  const analyticsConfig = useQuery(analyticsConfigQuery);

  const pipoMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.pipoMinThreshold;
  const pipoMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.pipoMaxThreshold;
  const wrMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrMinThreshold;
  const wrMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrMaxThreshold;
  const preOpMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpMinThreshold;
  const preOpMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpMaxThreshold;
  const pacuMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.pacuMinThreshold;
  const pacuMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.pacuMaxThreshold;
  const postOpMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.postOpMinThreshold;
  const postOpMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.postOpMaxThreshold;
  const preOpToDischargedMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpToDischargedMinThreshold;
  const preOpToDischargedMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.preOpToDischargedMaxThreshold;
  const inOrMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.inOrMinThreshold;
  const inOrMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.inOrMaxThreshold;
  const closingMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.closingMinThreshold;
  const closingMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.closingMaxThreshold;
  const procedureCompleteMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.procedureCompleteMinThreshold;
  const procedureCompleteMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.procedureCompleteMaxThreshold;
  const orReadyMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.orReadyMinThreshold;
  const orReadyMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.orReadyMaxThreshold;
  const piToStartMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToStartMinThreshold;
  const piToStartMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToStartMaxThreshold;
  const poToCutMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.poToCutMinThreshold;
  const poToCutMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.poToCutMaxThreshold;
  const startToCloseMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.startToCloseMinThreshold;
  const startToCloseMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.startToCloseMaxThreshold;
  const physicianPopiMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.physicianPopiMinThreshold;
  const physicianPopiMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.physicianPopiMaxThreshold;
  const fcotsGracePeriod = analyticsConfig?.data?.analyticsConfiguration?.fcotsGracePeriod;
  const piToPhyiMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToPhyiMinThreshold;
  const piToPhyiMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.piToPhyiMaxThreshold;
  const closingCompleteToPatientOutMinThreshold =
    analyticsConfig?.data?.analyticsConfiguration?.closingCompleteToPatientOutMinThreshold;
  const closingCompleteToPatientOutMaxThreshold =
    analyticsConfig?.data?.analyticsConfiguration?.closingCompleteToPatientOutMaxThreshold;
  const wrInToDischargeMinThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrInToDischargeMinThreshold;
  const wrInToDischargeMaxThreshold = analyticsConfig?.data?.analyticsConfiguration?.wrInToDischargeMaxThreshold;

  const { data, loading } = useQuery(physicianUtilizationAnalyticsQuery, {
    variables: {
      filter: {
        dateRange: dateRangeJSON,
        ...sanitizedOperationRoomsFilter,
        ...sanitizedPhysiciansFilter,
        ...pickFilter(rest),
        ...pickCptFilter(rest),
      },
      pipoMinThreshold,
      pipoMaxThreshold,
      wrMinThreshold,
      wrMaxThreshold,
      preOpMinThreshold,
      preOpMaxThreshold,
      pacuMinThreshold,
      pacuMaxThreshold,
      postOpMinThreshold,
      postOpMaxThreshold,
      preOpToDischargedMinThreshold,
      preOpToDischargedMaxThreshold,
      inOrMinThreshold,
      inOrMaxThreshold,
      closingMinThreshold,
      closingMaxThreshold,
      procedureCompleteMinThreshold,
      procedureCompleteMaxThreshold,
      orReadyMinThreshold,
      orReadyMaxThreshold,
      piToStartMinThreshold,
      piToStartMaxThreshold,
      poToCutMinThreshold,
      poToCutMaxThreshold,
      startToCloseMinThreshold,
      startToCloseMaxThreshold,
      physicianPopiMinThreshold,
      physicianPopiMaxThreshold,
      fcotsGracePeriod,
      piToPhyiMinThreshold,
      piToPhyiMaxThreshold,
      closingCompleteToPatientOutMinThreshold,
      closingCompleteToPatientOutMaxThreshold,
      wrInToDischargeMinThreshold,
      wrInToDischargeMaxThreshold,
      groupBy,
    },
    fetchPolicy: 'cache-and-network',
  });
  const scope: any = useScope();
  const optionalPacuSteps = usePacuOptionalProcedureSteps();
  const hasPostOp = scope?.hospital?.modules?.hasPostop;
  const analyticsV2PieCharts = scope?.hospital?.modules?.analyticsV2PieCharts || false;
  const [physicianId, setPhysicianId] = useState<{
    idNumber: number;
    idString: string;
  } | null>(null);

  const { data: totalsData, loading: totalsLoading } = useQuery(physicianUtilizationAnalyticsTotalsQuery, {
    variables: {
      filter: {
        dateRange: dateRangeJSON,
        ...sanitizedOperationRoomsFilter,
        ...sanitizedPhysiciansFilter,
        ...pickFilter(rest),
        ...pickCptFilter(rest),
      },
      pipoMinThreshold,
      pipoMaxThreshold,
      wrMinThreshold,
      wrMaxThreshold,
      preOpMinThreshold,
      preOpMaxThreshold,
      pacuMinThreshold,
      pacuMaxThreshold,
      postOpMinThreshold,
      postOpMaxThreshold,
      preOpToDischargedMinThreshold,
      preOpToDischargedMaxThreshold,
      inOrMinThreshold,
      inOrMaxThreshold,
      closingMinThreshold,
      closingMaxThreshold,
      procedureCompleteMinThreshold,
      procedureCompleteMaxThreshold,
      orReadyMinThreshold,
      orReadyMaxThreshold,
      piToStartMinThreshold,
      piToStartMaxThreshold,
      poToCutMinThreshold,
      poToCutMaxThreshold,
      startToCloseMinThreshold,
      startToCloseMaxThreshold,
      physicianPopiMinThreshold,
      physicianPopiMaxThreshold,
      fcotsGracePeriod,
      piToPhyiMinThreshold,
      piToPhyiMaxThreshold,
      closingCompleteToPatientOutMinThreshold,
      closingCompleteToPatientOutMaxThreshold,
      wrInToDischargeMinThreshold,
      wrInToDischargeMaxThreshold,
      groupBy,
    },
    fetchPolicy: 'cache-and-network',
  });
  const onPhysicianClick = (physicianId: string) => {
    const id = physicianId?.split('#')?.[0];
    setPhysicianId({ idNumber: Number(id), idString: physicianId });
  };

  const physicianUtilizationAnalyticsTotals = totalsData?.physicianUtilizationAnalyticsTotals;

  const table: TableType = toTableData(
    data?.physicianUtilizationAnalytics || [],
    physicianUtilizationAnalyticsTotals || [],
    onPhysicianClick,
    hasPostOp,
    entityLabel,
    optionalPacuSteps.names
  );

  const pieChartData = toPieChartData(
    data?.physicianUtilizationAnalytics || [],
    totalsData?.physicianUtilizationAnalyticsTotals || []
  );

  const onBack = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setPhysicianId(null);
  };

  const theme = useTheme();

  if (physicianId) {
    const physicianAnalytics = (data?.physicianUtilizationAnalytics || []).find(e => e?.id === physicianId.idString);

    const fcotsDelayAvg = physicianAnalytics?.fcotsDelayAvg;
    const fcotsDelayDist = physicianAnalytics?.fcotsDelayDist;
    const fcotsDelayReasons = physicianAnalytics?.fcotsDelayReasons;
    const proceduresOnTimePercentage = physicianAnalytics?.proceduresOnTimePercentage;

    return (
      <>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={onBack} style={{ marginBottom: 8 }}>
          Back to PACU Analytics
        </Button>
        <PacuPhaseDelayCharts source={physicianAnalytics} height={280} steps={optionalPacuSteps} />
        <PhysicianPacuPatientsAnalytics
          physicianId={physicianId.idNumber}
          physicianName={physicianAnalytics?.physician}
          dateRange={dateRange}
          groupBy={groupBy}
          entityLabel={entityLabel}
          filter={{
            dateRange: dateRangeJSON,
            ...sanitizedOperationRoomsFilter,
            ...sanitizedPhysiciansFilter,
            ...pickFilter(rest),
            ...pickCptFilter(rest),
          }}
        />
      </>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" m={2} style={{ gap: '1em' }}>
        <CircularProgress size={30} />
        <Typography variant="body1" color="textSecondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  const fcotsDelayAvg = totalsData?.physicianUtilizationAnalyticsTotals?.fcotsDelayAvg;
  const fcotsDelayDist = totalsData?.physicianUtilizationAnalyticsTotals?.fcotsDelayDist;
  const fcotsDelayReasons = totalsData?.physicianUtilizationAnalyticsTotals?.fcotsDelayReasons;
  const pacuDelayReasons = totalsData?.physicianUtilizationAnalyticsTotals?.pacuDelayReasons;
  const proceduresOnTimePercentage = totalsData?.physicianUtilizationAnalyticsTotals?.proceduresOnTimePercentage;

  const fcotsDelayDistWithPhysicians =
    fcotsDelayDist?.map((value, i) => {
      const physicianCount = {};

      const physicians =
        data?.physicianUtilizationAnalytics
          ?.filter(p => p.fcotsDelayDist?.[i] !== 0 && value !== 0 && p.fcotsDelayDist !== null)
          ?.map(p => p.physician) ?? [];

      physicians.forEach(physician => {
        const physicianData = data?.physicianUtilizationAnalytics?.find(p => p.physician === physician);
        if (physicianData) {
          physicianCount[physician] = physicianData.fcotsDelayDist?.[i];
        }
      });

      return {
        value,
        title: titles[i],
        physicians:
          physicians.map(physician => ({
            physician,
            count: physicianCount[physician],
          })) ?? [],
      };
    }) ?? [];

  const utilization = 50 * 100;

  const data2 = [
    { name: 'FCOTS', value: Math.round(proceduresOnTimePercentage), showInTooltip: true, showInLegend: true },
    { name: 'Rest', value: 100 - Math.round(proceduresOnTimePercentage), showInTooltip: false, showInLegend: false },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const aggregation = useContext(AggregationContext);

  const journeySteps = optionalPacuSteps.ready
    ? optionalPacuSteps
    : {
        ready: true,
        transferredToChair: true,
        transferredToPT: true,
        ptComplete: true,
        names: optionalPacuSteps.names,
      };
  const pacuMetrics = pacuJourneyMetrics(journeySteps, totalsData?.physicianUtilizationAnalyticsTotals, aggregation);

  const physicianRows = data?.physicianUtilizationAnalytics as PhysicianUtilizationTableData[] | undefined;
  const toThreshold = (value: unknown) => {
    if (value == null || value === '') {
      return undefined;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };
  const chairName = optionalPacuSteps.names.transferredToChair;
  const ptName = optionalPacuSteps.names.transferredToPT;
  const ptCompleteName = optionalPacuSteps.names.ptComplete;
  const milestoneCharts = [
    optionalPacuSteps.transferredToChair
      ? {
          title: `PACU In to ${chairName}`,
          configType: 'pacuInToChairTime' as const,
          greenLineY: toThreshold(analyticsConfig?.data?.analyticsConfiguration?.timeToChairThreshold),
          data: toMilestoneBarData(
            physicianRows,
            aggregation,
            'pacuInToTransferToChairAvg',
            'pacuInToTransferToChairMed'
          ),
        }
      : null,
    optionalPacuSteps.transferredToPT
      ? {
          title: `PACU In to ${ptName}`,
          configType: 'pacuInToPTStartTime' as const,
          greenLineY: toThreshold(analyticsConfig?.data?.analyticsConfiguration?.ptStartTimeThreshold),
          data: toMilestoneBarData(physicianRows, aggregation, 'pacuInToTransferToPTAvg', 'pacuInToTransferToPTMed'),
        }
      : null,
    optionalPacuSteps.ptComplete
      ? {
          title: `PACU In to ${ptCompleteName}`,
          configType: 'pacuInToPTCompleteTime' as const,
          greenLineY: toThreshold(analyticsConfig?.data?.analyticsConfiguration?.ptCompleteTimeThreshold),
          data: toMilestoneBarData(physicianRows, aggregation, 'pacuInToPTCompleteAvg', 'pacuInToPTCompleteMed'),
        }
      : null,
  ].flatMap(chart => (chart ? [chart] : []));

  // @ts-ignore
  // @ts-ignore
  return (
    <>
      {analyticsV2PieCharts &&
        (physicianUtilizationAnalyticsTotals ||
          fcotsDelayAvg ||
          fcotsDelayDist ||
          fcotsDelayReasons ||
          pacuDelayReasons) && (
          <>
            <Grid container spacing={1} style={{ marginBottom: 8 }}>
              <Grid item xs={12} md={3} style={{ display: 'flex', minWidth: 0 }}>
                <AnalyticsPanel title="Pacu Journey">
                  <PacuJourneyChart pacuTimes={pacuMetrics} stepNames={journeySteps.names} />
                </AnalyticsPanel>
              </Grid>
              {milestoneCharts.map(chart => (
                <Grid item xs={12} md={3} key={chart.title} style={{ display: 'flex', minWidth: 0 }}>
                  <AnalyticsPanel
                    title={chart.title}
                    action={
                      <Configuration
                        title={`${chart.title} Configuration`}
                        configType={chart.configType}
                        fieldLabel={`${chart.title} Threshold`}
                        size="small"
                      />
                    }
                  >
                    <PacuMilestoneBarChart data={chart.data} greenLineY={chart.greenLineY} />
                  </AnalyticsPanel>
                </Grid>
              ))}
            </Grid>
            <PacuPhaseDelayCharts source={physicianUtilizationAnalyticsTotals} steps={optionalPacuSteps} />
            <Button
              startIcon={<TableChart />}
              variant="contained"
              onClick={handleClickOpen}
              style={{ width: '100%', minHeight: 120 }}
            >
              View data in table
            </Button>
          </>
        )}

      <Dialog fullScreen open={open} onClose={handleClose} PaperProps={{ style: { overflow: 'hidden' } }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box px={2} pt={1}>
          <CptEligibleCaption
            active={cptFilterActive}
            retainedCases={physicianUtilizationAnalyticsTotals?.retainedCases}
            excludedCombinedCases={physicianUtilizationAnalyticsTotals?.excludedCombinedCases}
          />
        </Box>
        <TableWithColumnSelector
          configName="pacu-table-config"
          tableId="PACU-Analysis-table"
          tableName={`PACU Analysis by ${entityLabel}`}
          excelFileName={`PACU-Analysis-${format(dateRange.from, 'MM/DD/YYYY')}-${format(dateRange.to, 'MM/DD/YYYY')}`}
          disableHeaderTruncation
          {...table}
        />
      </Dialog>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flex: 1,
  },
  container: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(2, 1.5),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  leftTitle: {
    flex: 1,
    textAlign: 'left',
  },
  centerTitle: {
    flex: 1,
    textAlign: 'center',
  },
  rightTitle: {
    flex: 1,
    textAlign: 'right',
  },
}));

export default PacuPhysiciainUtilizationAnalytics;

export const formatDurationInMinutes = (duration?: number, decimals: number = 0) => {
  if (duration === undefined) {
    return '-';
  }

  return Math.round(duration).toFixed(decimals) + ' ' + (duration === 1 ? 'min' : 'mins');
};

const titles = ['0–5m', '5–10m', '10–20m', '20–30m', '30m+'];
